"use client";
import React, { useCallback, useEffect, useState } from "react";
import { authV1Headers } from "../lib/supabaseAuth";
import { Icon } from './Icon';

/**
 * Shared login form for Web (Next.js) + Admin SPA + Tenant SPA.
 *
 * The component encapsulates the GoTrue-facing flow:
 *   1. Password grant against `${platformOrigin}/auth/v1/token?grant_type=password`
 *   2. Optional `POST ${apiBase}/auth/session-bridge` to set the bp_platform
 *      cookie on `.bolloprint.com` (web/admin opt in via `bridgeAudience`).
 *   3. Optional invite flow: fetches invitation metadata and lets the user
 *      either accept with a self-chosen password (creates the auth.users row)
 *      or land on a Google handoff that promotes the GoTrue session post-OAuth.
 *
 * It deliberately stays out of:
 *   * Routing — caller owns navigation via `onSuccess(session, ctx)`.
 *   * i18n — caller passes `labels` for every visible string.
 *   * Forgot-password — caller hooks a `onForgotPassword` button; the actual
 *     recovery flow lives in each app (web/tenant route to their own page).
 */

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

export type GoTrueSession = {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  expires_at?: number;
  token_type?: string;
};

export type LoginFormBranding = {
  logoUrl: string;
  appName: string;
  loginTitle?: string;
  loginSubtitle?: string;
};

export type LoginFormLabels = {
  email: string;
  password: string;
  showPassword: string;
  hidePassword: string;
  submit: string;
  submitting: string;
  googleCta: string;
  or: string;
  forgotPassword?: string;
  noAccount?: string;
  createAccount?: string;
  // Invite flow
  invitation?: string;
  invitedTo?: string;
  invitedAs?: string;
  inviteEmailLabel?: string;
  inviteFirstName?: string;
  inviteFirstNameRequired?: string;
  inviteLastName?: string;
  invitePasswordExisting?: string;
  invitePasswordNew?: string;
  invitePasswordConfirm?: string;
  invitePasswordMismatch?: string;
  /** Receives the configured min length so the host can interpolate it into
   *  the translation. */
  invitePasswordTooShort?: (min: number) => string;
  /** Backend ``security.validate_password_strength`` (tenant) requires the
   *  same rules: ≥6 chars + 1 uppercase + 1 non-alphanumeric symbol. We
   *  mirror them client-side so the user gets immediate feedback instead
   *  of round-tripping. */
  invitePasswordMissingUppercase?: string;
  invitePasswordMissingSymbol?: string;
  acceptInvitation?: string;
  inviteCheckingMsg?: string;
  inviteLoadError?: string;
  goBack?: string;
  googleOnlyHint?: string;
  googleCreateHint?: string;
  // Errors
  invalidCredentials: string;
  networkError: string;
  bridgeError?: string;
  googleNotConfigured?: string;
};

export type InviteInfo = {
  client_name: string;
  workshop_slug: string;
  email: string;
  role: string;
  user_exists: boolean;
  user_has_password: boolean;
};

type LoginContext = {
  invite?: { token: string; info: InviteInfo };
  next?: string;
};

export type LoginFormProps = {
  branding: LoginFormBranding;
  labels: LoginFormLabels;

  /** Origin of the GoTrue auth endpoints (`https://bolloprint.com` in prod;
   *  the same origin as the SPA in dev when proxied). */
  platformOrigin: string;

  /** Where to land after the Google OAuth dance. Must be SPA-served (e.g.
   *  `${origin}/auth/callback`, `${origin}/session-bridge`). */
  googleRedirectTo: string;

  /** Capability flags — each app turns on the parts it needs. */
  enablePassword?: boolean;
  enableGoogle?: boolean;
  enableInviteFlow?: boolean;
  enableForgotPassword?: boolean;
  enableSignupLink?: boolean;

  /** When set, runs `POST ${apiBase}/auth/session-bridge` after password
   *  grant so the bp_platform cookie lands on `.bolloprint.com`. Tenant
   *  typically leaves this undefined (it stores tokens in localStorage). */
  bridgeAudience?: "web" | "admin";

  /** Base URL for the bridge call (default: `''`, same-origin). */
  apiBase?: string;

  /** External hooks for state the component does not own. */
  inviteToken?: string | null;
  next?: string | null;
  oauthError?: string | null;
  oauthErrorLabel?: (code: string) => string;

  /** Map a recovered InviteInfo's role into a human label. */
  inviteRoleLabel?: (role: string) => string;

  /** Endpoints used by the invite flow. Tenant sets these to its own paths;
   *  web/admin do not enable invite, so they can leave them undefined. */
  inviteFetchPath?: (token: string) => string;
  inviteAcceptPath?: (token: string) => string;

  /** Minimum password length enforced client-side before submitting the
   *  invite. Should mirror GoTrue's ``GOTRUE_PASSWORD_MIN_LENGTH`` (default
   *  6 — what GoTrue ships with when the env var is not set). */
  passwordMinLength?: number;

  /** Called once the user is authenticated. Receives the GoTrue tokens and
   *  any context the form picked up (resolved invite, deep-link target). */
  onSuccess: (session: GoTrueSession, ctx: LoginContext) => void;

  /** Called when the user clicks the "forgot password" / "create account"
   *  link. The component does NOT navigate — the host app does. */
  onForgotPassword?: () => void;
  onSignupClick?: () => void;

  /** Fired exactly once when the invite-metadata fetch fails (404, expired,
   *  revoked, network). Host should toast "Invitación no válida" and navigate
   *  to ``/login`` (stripping the ``?invite=...`` query). When provided, the
   *  component does NOT render its own inline error page. */
  onInviteInvalid?: () => void;

  /** Translates a typed backend error (`api_error`'s `{code, params}`) shown
   *  by the invitation step. Return `null` when there is no translation: the
   *  backend's Spanish default is shown then. Omit (admin) → that default. */
  translateErrorCode?: (code: string, params: Record<string, unknown> | null) => string | null;
};

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

async function passwordGrant(
  platformOrigin: string,
  email: string,
  password: string,
): Promise<GoTrueSession | null> {
  try {
    const res = await fetch(`${platformOrigin}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: authV1Headers(),
      body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as GoTrueSession;
    if (!data.access_token || !data.refresh_token) return null;
    return data;
  } catch {
    return null;
  }
}

async function sessionBridge(
  apiBase: string,
  audience: "web" | "admin",
  session: GoTrueSession,
): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase}/api/auth/session-bridge`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_in: session.expires_in,
        audience,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function buildGoogleHref(platformOrigin: string, redirectTo: string): string {
  return (
    `${platformOrigin}/auth/v1/authorize?provider=google` +
    `&redirect_to=${encodeURIComponent(redirectTo)}`
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg fill="none" height="20" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
      <path d="M23.7663 12.2764C23.7663 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.7663 15.9274 23.7663 12.2764Z" fill="#4285F4" />
      <path d="M12.2399 24.0008C15.4765 24.0008 18.2059 22.9382 20.1944 21.1039L16.3274 18.1055C15.2516 18.8375 13.8626 19.252 12.2444 19.252C9.11377 19.252 6.45935 17.1399 5.50693 14.3003H1.51648V17.3912C3.5536 21.4434 7.70279 24.0008 12.2399 24.0008Z" fill="#34A853" />
      <path d="M5.50242 14.3003C5.00225 12.8099 5.00225 11.1961 5.50242 9.70575V6.61481H1.51643C-0.185586 10.0056 -0.185586 14.0004 1.51643 17.3912L5.50242 14.3003Z" fill="#FBBC05" />
      <path d="M12.2399 4.74966C13.9508 4.7232 15.6043 5.36697 16.8433 6.54867L20.2694 3.12262C18.0999 1.0855 15.2207 -0.0344664 12.2399 0.000808666C7.70279 0.000808666 3.5536 2.55822 1.51648 6.61481L5.50254 9.70575C6.45053 6.86173 9.10931 4.74966 12.2399 4.74966Z" fill="#EA4335" />
    </svg>
  );
}

const inputClass =
  "w-full rounded-xl border border-border-subtle bg-surface-light py-3 px-3 text-sm text-text-main shadow-sm outline-none transition-all placeholder-slate-400 focus:border-primary focus:ring-1 focus:ring-primary dark:border-border-subtle-dark dark:bg-surface-dark dark:text-white dark:placeholder-slate-600 hover:border-border-strong dark:hover:border-border-strong-dark disabled:opacity-50";

const labelClass =
  "mb-1.5 block truncate text-xs font-semibold uppercase tracking-wider text-text-sub dark:text-slate-400";

const submitBtnClass =
  "inline-flex h-10 w-full items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] hover:bg-primary-dark active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 !normal-case";

const googleBtnClass =
  "relative flex w-full items-center justify-center rounded-xl border border-border-subtle bg-white py-3 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:border-primary hover:shadow-md dark:border-border-subtle-dark dark:bg-white dark:text-slate-700 disabled:opacity-50";

const errorBoxClass =
  "rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200";

const oauthErrorBoxClass =
  "mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100";

function AuthShell({
  branding,
  children,
}: {
  branding: LoginFormBranding;
  children: React.ReactNode;
}) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-background-light dark:bg-background-dark">
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/15 blur-3xl dark:bg-primary/20"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-secondary/20 blur-3xl"
        aria-hidden
      />
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-2xl border border-border-subtle bg-white p-8 shadow-card dark:border-border-subtle-dark dark:bg-surface-dark dark:shadow-card-dark">
          <div className="mb-6 flex flex-col items-center gap-3">
            <div className="h-24 w-24 flex items-center justify-center overflow-hidden rounded-lg">
              <img src={branding.logoUrl} alt={branding.appName} className="w-full h-full object-contain" />
            </div>
            <span className="font-display text-3xl font-bold tracking-tight text-slate-800 dark:text-white">
              {branding.appName}
            </span>
            {branding.loginTitle ? (
              <h1 className="text-lg font-bold text-slate-700 dark:text-slate-200">{branding.loginTitle}</h1>
            ) : null}
            {branding.loginSubtitle ? (
              <p className="text-center text-sm text-text-sub dark:text-slate-400">
                {branding.loginSubtitle}
              </p>
            ) : null}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────

export default function LoginForm(props: LoginFormProps) {
  const {
    branding,
    labels,
    platformOrigin,
    googleRedirectTo,
    enablePassword = false,
    enableGoogle = true,
    enableInviteFlow = false,
    enableForgotPassword = false,
    enableSignupLink = false,
    bridgeAudience,
    apiBase = "",
    inviteToken,
    next,
    oauthError,
    oauthErrorLabel,
    inviteRoleLabel,
    inviteFetchPath,
    inviteAcceptPath,
    passwordMinLength = 8,
    onSuccess,
    onForgotPassword,
    onSignupClick,
    onInviteInvalid,
    translateErrorCode,
  } = props;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Invite-flow state (only used when enableInviteFlow + inviteToken)
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteLoadError, setInviteLoadError] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [invitePassword, setInvitePassword] = useState("");
  const [inviteConfirmPassword, setInviteConfirmPassword] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");

  const activeInviteToken =
    enableInviteFlow && inviteToken && inviteToken.trim() ? inviteToken.trim() : null;

  // Fetch invite metadata once when a token is present.
  useEffect(() => {
    if (!activeInviteToken || !inviteFetchPath) {
      setInviteInfo(null);
      setInviteLoadError(null);
      setInviteLoading(false);
      return;
    }
    let cancelled = false;
    setInviteLoading(true);
    setInviteLoadError(null);
    setInviteInfo(null);
    (async () => {
      try {
        const res = await fetch(`${apiBase}${inviteFetchPath(activeInviteToken)}`);
        if (!res.ok) throw new Error(labels.inviteLoadError || "Invalid invitation");
        const data = (await res.json()) as InviteInfo;
        if (!cancelled) setInviteInfo(data);
      } catch (e) {
        if (!cancelled) {
          setInviteLoadError(e instanceof Error ? e.message : labels.inviteLoadError || "Error");
        }
      } finally {
        if (!cancelled) setInviteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeInviteToken, inviteFetchPath, apiBase, labels.inviteLoadError]);

  const finalize = useCallback(
    async (session: GoTrueSession, ctxExtra: Partial<LoginContext> = {}) => {
      if (bridgeAudience) {
        const ok = await sessionBridge(apiBase, bridgeAudience, session);
        if (!ok) {
          setErr(labels.bridgeError || labels.networkError);
          setLoading(false);
          return;
        }
      }
      onSuccess(session, {
        next: next || undefined,
        ...ctxExtra,
      });
    },
    [bridgeAudience, apiBase, onSuccess, next, labels.bridgeError, labels.networkError],
  );

  const onSubmitPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErr("");
      setLoading(true);
      const session = await passwordGrant(platformOrigin, email, password);
      if (!session) {
        setErr(labels.invalidCredentials);
        setLoading(false);
        return;
      }
      await finalize(session);
    },
    [platformOrigin, email, password, finalize, labels.invalidCredentials],
  );

  const onSubmitInvite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!activeInviteToken || !inviteInfo || !inviteAcceptPath) return;
      setErr("");
      // Client-side validation when the user is creating a brand-new account
      // (existing users with a password are just logging in; they don't need
      // confirmation or length checks beyond GoTrue's own).
      if (!inviteInfo.user_exists) {
        if (!inviteFirstName.trim()) {
          setErr(labels.inviteFirstNameRequired || labels.invalidCredentials);
          return;
        }
        if (invitePassword.length < passwordMinLength) {
          setErr(
            labels.invitePasswordTooShort
              ? labels.invitePasswordTooShort(passwordMinLength)
              : labels.invalidCredentials,
          );
          return;
        }
        // Mirror tenant ``security.validate_password_strength``: at least
        // one uppercase letter and one non-alphanumeric symbol.
        if (!/[A-Z]/.test(invitePassword)) {
          setErr(labels.invitePasswordMissingUppercase || labels.invalidCredentials);
          return;
        }
        if (!/[^A-Za-z0-9]/.test(invitePassword)) {
          setErr(labels.invitePasswordMissingSymbol || labels.invalidCredentials);
          return;
        }
        if (invitePassword !== inviteConfirmPassword) {
          setErr(labels.invitePasswordMismatch || labels.invalidCredentials);
          return;
        }
      }
      setLoading(true);
      try {
        const body: Record<string, string> = { password: invitePassword };
        if (!inviteInfo.user_exists) {
          if (inviteFirstName.trim()) body.first_name = inviteFirstName.trim();
          if (inviteLastName.trim()) body.last_name = inviteLastName.trim();
          // username is intentionally NOT sent — the backend auto-derives it
          // from the email's local-part, deduplicating with _1/_2/... when
          // collisions exist (see ``accept_invitation`` in tenant auth.py).
        }
        const res = await fetch(`${apiBase}${inviteAcceptPath(activeInviteToken)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          // FastAPI's `api_error()` helper returns `detail` as an object
          // ({code, params, message}); plain HTTPException returns a string.
          // Render only the human message in either case to avoid React #31
          // ("Objects are not valid as a React child").
          const rawDetail = (data as { detail?: unknown }).detail;
          // A typed error in the host's language when it can translate it.
          const typed =
            rawDetail && typeof rawDetail === "object" && "code" in rawDetail
              ? (rawDetail as { code?: unknown; params?: unknown })
              : null;
          const translated =
            typed && typeof typed.code === "string" && translateErrorCode
              ? translateErrorCode(
                  typed.code,
                  typed.params && typeof typed.params === "object"
                    ? (typed.params as Record<string, unknown>)
                    : null,
                )
              : null;
          const detailMsg =
            typeof rawDetail === "string"
              ? rawDetail
              : rawDetail && typeof rawDetail === "object" && "message" in rawDetail
                ? String((rawDetail as { message: unknown }).message ?? "")
                : "";
          setErr(translated || detailMsg || labels.invalidCredentials);
          setLoading(false);
          return;
        }
        const data = (await res.json()) as { access_token: string; refresh_token?: string };
        const session: GoTrueSession = {
          access_token: data.access_token,
          refresh_token: data.refresh_token || "",
        };
        await finalize(session, {
          invite: { token: activeInviteToken, info: inviteInfo },
        });
      } catch {
        setErr(labels.networkError);
        setLoading(false);
      }
    },
    [
      activeInviteToken,
      inviteInfo,
      inviteAcceptPath,
      apiBase,
      invitePassword,
      inviteConfirmPassword,
      inviteFirstName,
      inviteLastName,
      passwordMinLength,
      finalize,
      translateErrorCode,
      labels.invalidCredentials,
      labels.inviteFirstNameRequired,
      labels.invitePasswordMismatch,
      labels.invitePasswordMissingSymbol,
      labels.invitePasswordMissingUppercase,
      labels.invitePasswordTooShort,
      labels.networkError,
    ],
  );

  // Fire onInviteInvalid exactly once when the invite-metadata fetch fails
  // (token unknown, expired, revoked, network). Host typically reacts with a
  // toast + navigate('/login'), so we skip rendering the inline error page
  // when the callback is wired (handled in the JSX below).
  useEffect(() => {
    if (inviteLoadError && onInviteInvalid) {
      onInviteInvalid();
    }
  }, [inviteLoadError, onInviteInvalid]);

  const startGoogle = useCallback(() => {
    if (!enableGoogle) return;
    if (!platformOrigin) {
      setErr(labels.googleNotConfigured || labels.networkError);
      return;
    }
    // Persist invite token across the OAuth round-trip; AuthCallbackPage of
    // the host app reads it and POSTs to /auth/invite/{token}/accept-supabase.
    if (activeInviteToken) {
      try {
        sessionStorage.setItem("bolloprint_pending_invite", activeInviteToken);
      } catch {
        /* private mode: harmless */
      }
    }
    window.location.assign(buildGoogleHref(platformOrigin, googleRedirectTo));
  }, [enableGoogle, platformOrigin, googleRedirectTo, activeInviteToken, labels]);

  // ── Invite mode ─────────────────────────────────────────────────────
  if (activeInviteToken) {
    if (inviteLoading) {
      return (
        <AuthShell branding={branding}>
          <div className="flex flex-col items-center gap-3 py-6">
            <Icon name="progress_activity" className="animate-spin text-primary !text-[40px]" />
            <p className="text-text-sub dark:text-slate-400 text-sm">{labels.inviteCheckingMsg}</p>
          </div>
        </AuthShell>
      );
    }
    if (inviteLoadError || !inviteInfo) {
      // Host owns the redirect + toast — render nothing while the navigation
      // takes effect (the useEffect above fires onInviteInvalid).
      if (onInviteInvalid) return null;
      return (
        <AuthShell branding={branding}>
          <h1 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">{labels.invitation}</h1>
          <p className="text-text-sub dark:text-slate-400 text-sm mb-6">{inviteLoadError || labels.inviteLoadError}</p>
          {labels.goBack ? (
            <button type="button" onClick={onForgotPassword} className={submitBtnClass}>
              {labels.goBack}
            </button>
          ) : null}
        </AuthShell>
      );
    }

    const showPasswordForm = inviteInfo.user_exists ? inviteInfo.user_has_password : true;
    const showGoogleForInvite = enableGoogle && (!inviteInfo.user_exists || !inviteInfo.user_has_password);

    return (
      <AuthShell branding={branding}>
        <div className="flex flex-col gap-2 mb-6 w-full">
          <h1 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">{labels.invitation}</h1>
          <p className="text-text-sub dark:text-slate-400 text-sm leading-relaxed">
            {labels.invitedTo}{" "}
            <span className="text-primary font-semibold">{inviteInfo.client_name}</span>
          </p>
          <p className="text-xs text-text-sub dark:text-slate-500">
            {labels.inviteEmailLabel}:{" "}
            <span className="font-medium text-slate-700 dark:text-slate-300">{inviteInfo.email}</span>
            <br />
            {labels.invitedAs}: {inviteRoleLabel ? inviteRoleLabel(inviteInfo.role) : inviteInfo.role}
          </p>
        </div>

        {err ? <p className={`${errorBoxClass} mb-4`}>{err}</p> : null}

        {/* Google first — same order as the regular login mode below. */}
        {showGoogleForInvite ? (
          <div className="w-full flex flex-col gap-3">
            {!inviteInfo.user_exists && labels.googleCreateHint ? (
              <p className="text-xs text-text-sub dark:text-slate-400 text-center">{labels.googleCreateHint}</p>
            ) : null}
            {inviteInfo.user_exists && !inviteInfo.user_has_password && labels.googleOnlyHint ? (
              <p className="text-xs text-text-sub dark:text-slate-400 text-center">{labels.googleOnlyHint}</p>
            ) : null}
            <button type="button" onClick={startGoogle} disabled={loading} className={googleBtnClass}>
              <div className="absolute left-5 flex items-center justify-center"><GoogleIcon /></div>
              <span className="pl-6">{labels.googleCta}</span>
            </button>
          </div>
        ) : null}

        {showGoogleForInvite && showPasswordForm ? (
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border-subtle dark:border-border-subtle-dark" />
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-wider">
              <span className="bg-white px-2 text-text-sub dark:bg-surface-dark dark:text-slate-400">
                {labels.or}
              </span>
            </div>
          </div>
        ) : null}

        {showPasswordForm ? (
          <form onSubmit={onSubmitInvite} className="space-y-4">
            {!inviteInfo.user_exists ? (
              <>
                <input
                  type="text"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  placeholder={labels.inviteFirstName}
                  className={inputClass}
                  disabled={loading}
                  required
                  minLength={1}
                  autoComplete="given-name"
                />
                <input
                  type="text"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  placeholder={labels.inviteLastName}
                  className={inputClass}
                  disabled={loading}
                  autoComplete="family-name"
                />
              </>
            ) : null}
            <input
              type="password"
              value={invitePassword}
              onChange={(e) => setInvitePassword(e.target.value)}
              placeholder={inviteInfo.user_exists ? labels.invitePasswordExisting : labels.invitePasswordNew}
              className={inputClass}
              disabled={loading}
              required
              minLength={passwordMinLength}
              autoComplete={inviteInfo.user_exists ? "current-password" : "new-password"}
            />
            {!inviteInfo.user_exists ? (
              <input
                type="password"
                value={inviteConfirmPassword}
                onChange={(e) => setInviteConfirmPassword(e.target.value)}
                placeholder={labels.invitePasswordConfirm}
                className={inputClass}
                disabled={loading}
                required
                minLength={passwordMinLength}
                autoComplete="new-password"
              />
            ) : null}
            <button type="submit" disabled={loading} className={submitBtnClass}>
              {loading ? labels.submitting : labels.acceptInvitation}
            </button>
          </form>
        ) : null}
      </AuthShell>
    );
  }

  // ── Normal login mode ───────────────────────────────────────────────
  return (
    <AuthShell branding={branding}>
      {oauthError && oauthErrorLabel ? (
        <p className={oauthErrorBoxClass}>{oauthErrorLabel(oauthError)}</p>
      ) : null}

      {enableGoogle ? (
        <button type="button" onClick={startGoogle} disabled={loading} className={googleBtnClass}>
          <div className="absolute left-5 flex items-center justify-center"><GoogleIcon /></div>
          <span className="pl-6">{labels.googleCta}</span>
        </button>
      ) : null}

      {enableGoogle && enablePassword ? (
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border-subtle dark:border-border-subtle-dark" />
          </div>
          <div className="relative flex justify-center text-xs uppercase tracking-wider">
            <span className="bg-white px-2 text-text-sub dark:bg-surface-dark dark:text-slate-400">
              {labels.or}
            </span>
          </div>
        </div>
      ) : null}

      {enablePassword ? (
        <form onSubmit={onSubmitPassword} className="space-y-4">
          {err ? <p className={errorBoxClass}>{err}</p> : null}
          <div>
            <label className={labelClass}>{labels.email}</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              className={inputClass}
            />
          </div>
          <div>
            <div className="relative mb-1.5 flex min-h-[16px] items-center justify-between">
              <label className={`${labelClass} pr-8`}>{labels.password}</label>
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-xs font-medium text-text-sub dark:text-slate-400 hover:text-primary"
              >
                {showPw ? labels.hidePassword : labels.showPassword}
              </button>
            </div>
            <input
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              className={`${inputClass} pr-12`}
            />
          </div>
          <button type="submit" disabled={loading} className={submitBtnClass}>
            {loading ? labels.submitting : labels.submit}
          </button>
        </form>
      ) : null}

      {enableForgotPassword && labels.forgotPassword && onForgotPassword ? (
        <p className="mt-6 text-center text-sm text-text-sub dark:text-slate-400">
          <button type="button" onClick={onForgotPassword} className="font-medium text-primary hover:underline">
            {labels.forgotPassword}
          </button>
        </p>
      ) : null}

      {enableSignupLink && labels.noAccount && labels.createAccount && onSignupClick ? (
        <p className="mt-4 text-center text-sm text-text-sub dark:text-slate-400">
          {labels.noAccount}{" "}
          <button type="button" onClick={onSignupClick} className="font-semibold text-primary hover:underline">
            {labels.createAccount}
          </button>
        </p>
      ) : null}
    </AuthShell>
  );
}
